/**
 * Aba "Fumaça" da barra lateral: liga a camada, mostra o que está no quadro
 * atual (quantas áreas, quanto de área, ou por que não há nada) e explica de
 * onde vem o contorno.
 */
import { CircleQuestionMark, ExternalLink, Haze, RefreshCw } from 'lucide-react';
import { formatInt } from '../lib/format';
import { formatRelative, formatTimeBrt } from '../lib/time';
import { shownTimeOf, useTimeStore } from '../state/time';
import { useNow } from '../ui/hooks';
import { Button, Hint, ProgressBar, Section, Slider, Toggle } from '../ui/primitives';
import { BASE_FUMACA, NOAA_ADP_URL, type QuadroFumaca } from './api';
import { COR_FUMACA } from './layer';
import { LIMITES_OPACIDADE, useSmokeStore, type FumacaNoQuadro } from './store';
import { carregarFumaca, ultimoQuadro } from './sync';
import { avisoDeFrescor, quadroParaInstante, semDeteccao, TOLERANCIA_MS } from './tempo';

/** Frase do quadro exibido. */
export function fraseDoQuadro(q: QuadroFumaca | null, noQuadro: FumacaNoQuadro, instante: number | null): string {
  if (!q) {
    return instante === null
      ? '—'
      : `Sem quadro da NOAA a até ${Math.round(TOLERANCIA_MS / 60_000)} min deste horário: o mapa fica sem fumaça.`;
  }
  if (noQuadro.estado === 'carregando') return 'Baixando o contorno deste horário…';
  if (noQuadro.estado === 'falhou') return 'O contorno deste horário não carregou.';
  if (semDeteccao(q)) return 'Sol baixo ou noite: a NOAA só garante a detecção de fumaça com o sol alto (cerca de 8h às 16h).';
  if (q.n === 0) return 'Nenhuma fumaça detectada neste horário.';
  const areas = q.n === 1 ? '1 área' : `${formatInt(q.n)} áreas`;
  // Pixels isolados somam poucos km²: arredondar para a centena daria "cerca de 0".
  const total = q.km2 < 100 ? 'menos de 100 km²' : `cerca de ${formatInt(Math.round(q.km2 / 100) * 100)} km²`;
  return `${areas} com fumaça, ${total} no total.`;
}

function Legenda() {
  return (
    <div className="flex items-center gap-2 text-xs text-ink-200">
      <svg width="28" height="16" viewBox="0 0 28 16" aria-hidden className="shrink-0">
        <path d="M3 9 C5 3, 12 2, 17 4 S26 7, 24 11 S12 15, 7 13 S1 12, 3 9 Z" fill="rgba(243,223,191,0.22)" stroke="rgba(24,18,12,0.6)" strokeWidth="3.2" />
        <path d="M3 9 C5 3, 12 2, 17 4 S26 7, 24 11 S12 15, 7 13 S1 12, 3 9 Z" fill="none" stroke={COR_FUMACA} strokeWidth="1.4" />
      </svg>
      Área em que o satélite detectou fumaça
    </div>
  );
}

export function SmokePanel() {
  const visivel = useSmokeStore((s) => s.visivel);
  const opacidade = useSmokeStore((s) => s.opacidade);
  const situacao = useSmokeStore((s) => s.situacao);
  const indice = useSmokeStore((s) => s.indice);
  const noQuadro = useSmokeStore((s) => s.noQuadro);
  const progresso = useSmokeStore((s) => s.progresso);
  const { setVisivel, setOpacidade } = useSmokeStore.getState();
  const now = useNow(60_000);
  const instante = useTimeStore((s) => shownTimeOf(s));
  const quadros = indice?.quadros ?? [];
  const q = quadroParaInstante(
    quadros,
    quadros.map((x) => x.t),
    instante,
  );
  const ultimo = ultimoQuadro(indice);
  const aviso = avisoDeFrescor(ultimo, now);
  const baixando = progresso.total > 0 && progresso.baixados + progresso.falhas < progresso.total;

  if (!BASE_FUMACA) {
    return (
      <Section title="Fumaça" icon={Haze}>
        <Hint>A camada de fumaça está desligada nesta versão.</Hint>
      </Section>
    );
  }

  return (
    <div>
      <Section title="Fumaça" icon={Haze}>
        <Toggle
          label="Fumaça vista pelo GOES-19"
          checked={visivel}
          onChange={setVisivel}
          description="O contorno da fumaça detectada pela NOAA, a cada 10 min, animado junto com o satélite."
        />

        {visivel && aviso && (
          <div className="rounded-lg border border-alerta-400/40 bg-alerta-400/10 px-2.5 py-1.5 text-[11px] leading-snug text-alerta-200">
            {aviso}
          </div>
        )}
        {visivel && situacao.estado === 'error' && !indice && (
          <div className="rounded-lg border border-alerta-400/40 bg-alerta-400/10 px-2.5 py-1.5 text-[11px] leading-snug text-alerta-200">
            Não deu para baixar a lista de quadros da fumaça: {situacao.erro}
          </div>
        )}

        {visivel && (
          <>
            <Legenda />
            {indice && (
              <div className="rounded-lg border border-ink-700/70 bg-ink-800/50 px-2.5 py-1.5 text-xs leading-snug text-ink-200">
                {q && <span className="mr-1 font-medium text-white tabular-nums">{formatTimeBrt(q.t)}</span>}
                {fraseDoQuadro(q, noQuadro, instante)}
              </div>
            )}
            <Slider
              label="Opacidade"
              value={opacidade}
              {...LIMITES_OPACIDADE}
              onChange={setOpacidade}
              format={(v) => `${Math.round(v * 100)}%`}
            />
            {baixando && (
              <div className="space-y-1">
                <ProgressBar value={progresso.total ? progresso.baixados / progresso.total : 0} tone="fogo" />
                <div className="text-[11px] text-ink-300 tabular-nums">
                  Baixando os contornos: {formatInt(progresso.baixados)} de {formatInt(progresso.total)}
                </div>
              </div>
            )}
            <Hint>Toque numa área para ver o tamanho dela.</Hint>
          </>
        )}
      </Section>

      <Section title="Dados" icon={RefreshCw} defaultOpen={false} storageKey="fumaca-dados">
        <div className="space-y-1.5 text-xs text-ink-300">
          {situacao.estado === 'loading' && <div>Baixando a lista de quadros…</div>}
          {situacao.estado === 'error' && <div className="text-alerta-300">Falha ao atualizar: {situacao.erro}</div>}
          {indice && (
            <div>
              {formatInt(quadros.length)} quadros nas últimas 48 h
              {ultimo !== null && <> · o mais novo é de {formatTimeBrt(ultimo)} ({formatRelative(ultimo, now)})</>}
            </div>
          )}
          <Button icon={RefreshCw} onClick={() => void carregarFumaca(true)}>
            Atualizar agora
          </Button>
        </div>
      </Section>

      <Section title="O que é" icon={CircleQuestionMark} defaultOpen={false} storageKey="fumaca-oque">
        <div className="space-y-2 text-xs leading-relaxed text-ink-300">
          <p>
            O GOES-19 compara a luz que volta da Terra em várias cores (do azul ao infravermelho). Fumaça espalha a luz de
            um jeito diferente da nuvem e do chão, e o algoritmo da NOAA marca, pixel a pixel (cerca de 2 km), onde há
            fumaça. O contorno no mapa é a borda dessas marcas, sem nada filtrado.
          </p>
          <p>
            <strong>Só com o sol alto</strong> (mais de 30° acima do horizonte, mais ou menos das 8h às 16h): com o sol
            baixo a própria NOAA classifica a detecção como degradada — ela confunde o amanhecer com fumaça —, e o mapa
            fica sem contorno. Debaixo de nuvem a fumaça também não é vista, e fumaça fina pode escapar ou aparecer em
            pedaços.
          </p>
          <p>
            Fumaça não é foco: o foco de queimada é o ponto quente no chão; a fumaça é o que sobe dele e viaja com o vento,
            às vezes por centenas de quilômetros.
          </p>
          <p className="text-ink-400">
            Fonte: NOAA — GOES-19 ABI, produto de detecção de aerossóis (ADP), máscara de fumaça. O AmaView guarda as
            últimas 48 h.{' '}
            <a
              href={NOAA_ADP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 text-fogo-400 hover:underline"
            >
              dados da NOAA
              <ExternalLink size={10} />
            </a>
          </p>
        </div>
      </Section>
    </div>
  );
}
